import jsPDF from "jspdf";
import { loadLogoForPdf } from "../convocations/logoLoader";

const fmtDate = (iso) => {
  if (!iso) return "____ / ____ / ________";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return iso; }
};

/**
 * Genera il cartellino di tesseramento FIGC - Settore Giovanile e Scolastico
 * Compila i dati anagrafici del giocatore + spazi firma genitori e società.
 * Se `signature` è presente, aggiunge in calce il riferimento alla firma digitale OTP.
 */
export async function downloadFigcCartellinoPdf({ team, player, parentName, signature }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 40;
  let y = margin;

  // Header
  const logo = await loadLogoForPdf(team);
  if (logo) {
    try { doc.addImage(logo.dataUrl, logo.format, margin, y, 50, 50); } catch (e) { console.warn("logo:", e); }
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(60);
  doc.text("F.I.G.C.", pageW - margin, y + 12, { align: "right" });
  doc.text("Settore Giovanile e Scolastico", pageW - margin, y + 26, { align: "right" });
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(`Stagione Sportiva ${team?.season || "___/___"}`, pageW - margin, y + 40, { align: "right" });

  y += 70;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(20);
  doc.text("CARTELLINO DI TESSERAMENTO", pageW / 2, y, { align: "center" });
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("Modulo richiesta tesseramento per attività giovanile", pageW / 2, y + 14, { align: "center" });
  y += 36;

  // Society block
  doc.setDrawColor(180);
  doc.setLineWidth(0.5);
  doc.rect(margin, y, pageW - margin * 2, 70);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("SOCIETÀ RICHIEDENTE", margin + 8, y + 14);
  doc.setFont("helvetica", "normal");
  const society = team?.legal_name || team?.name || "";
  doc.text(`Denominazione: ${society}`, margin + 8, y + 30);
  doc.text(`Matricola FIGC: ${team?.figc_affiliation_code || "________________"}`, margin + 8, y + 44);
  doc.text(`C.F. / P.IVA: ${team?.fiscal_code || team?.vat_number || "________________"}`, margin + 8, y + 58);
  doc.text(`Categoria: ${team?.category || ""}`, pageW - margin - 200, y + 30);
  doc.text(`Sede: ${team?.address || "________________"}`, pageW - margin - 200, y + 44);
  y += 84;

  // Player block
  doc.setFont("helvetica", "bold");
  doc.text("DATI ANAGRAFICI DEL CALCIATORE / CALCIATRICE", margin, y);
  y += 12;
  doc.setLineWidth(0.5);

  const drawField = (label, value, x, w) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(120);
    doc.text(label.toUpperCase(), x, y);
    doc.setFontSize(10);
    doc.setTextColor(20);
    doc.text(String(value || ""), x, y + 16);
    doc.setDrawColor(220);
    doc.line(x, y + 20, x + w, y + 20);
  };

  const colW = (pageW - margin * 2 - 16) / 3;
  drawField("Cognome", player?.last_name, margin, colW);
  drawField("Nome", player?.first_name, margin + colW + 8, colW);
  drawField("Data di nascita", fmtDate(player?.birth_date), margin + (colW + 8) * 2, colW);
  y += 36;
  drawField("Luogo di nascita", player?.birth_place || "", margin, colW);
  drawField("Codice Fiscale", player?.fiscal_code || "", margin + colW + 8, colW);
  drawField("Cittadinanza", player?.citizenship || "Italiana", margin + (colW + 8) * 2, colW);
  y += 36;
  drawField("Indirizzo residenza", player?.address || "", margin, colW * 2 + 8);
  drawField("Comune", player?.city || "", margin + (colW + 8) * 2, colW);
  y += 36;
  drawField("Telefono", player?.parent_phone || "", margin, colW);
  drawField("Email", player?.parent_email || "", margin + colW + 8, colW * 2);
  y += 30;

  // Parent / tutor block
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("ESERCENTI LA POTESTÀ GENITORIALE", margin, y);
  y += 12;
  drawField("Cognome e Nome del Padre", "", margin, colW + (colW + 8) / 2);
  drawField("Cognome e Nome della Madre", "", margin + colW + 8 + (colW + 8) / 2, colW + (colW + 8) / 2);
  y += 36;

  // Consents
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(60);
  const consents = [
    "□ Autorizziamo il tesseramento alla F.I.G.C. - L.N.D. - Settore Giovanile e Scolastico per la stagione in corso.",
    "□ Dichiariamo di aver ricevuto e accettato lo Statuto, il Regolamento e i Codici Sportivi vigenti.",
    "□ Acconsentiamo al trattamento dei dati personali ai sensi del Reg. UE 679/2016 (GDPR).",
    "□ Autorizziamo l'uso dell'immagine del minore per la pubblicazione su sito/social della società.",
  ];
  consents.forEach((c) => {
    doc.text(c, margin, y, { maxWidth: pageW - margin * 2 });
    y += 14;
  });
  y += 8;

  // Signatures — 3 columns (Padre / Madre / Presidente) per documentazione ufficiale FIGC
  doc.setTextColor(20);
  doc.setFontSize(9);
  doc.setLineWidth(0.5);
  doc.setDrawColor(120);
  const colGap = 18;
  const sigW = (pageW - margin * 2 - colGap * 2) / 3;
  const sigYLine = y + 30;
  // Padre
  doc.line(margin, sigYLine, margin + sigW, sigYLine);
  // Madre
  doc.line(margin + sigW + colGap, sigYLine, margin + sigW * 2 + colGap, sigYLine);
  // Presidente Società
  doc.line(margin + (sigW + colGap) * 2, sigYLine, margin + sigW * 3 + colGap * 2, sigYLine);
  doc.setFontSize(8);
  doc.text("Firma del Padre", margin, sigYLine + 12);
  doc.text("Firma della Madre", margin + sigW + colGap, sigYLine + 12);
  doc.text("Timbro e firma del Presidente", margin + (sigW + colGap) * 2, sigYLine + 12);
  doc.setFontSize(7);
  doc.setTextColor(120);
  doc.text("(o del tutore esercente la potestà)", margin, sigYLine + 22);
  doc.text("(o del tutore esercente la potestà)", margin + sigW + colGap, sigYLine + 22);
  doc.text("della Società sportiva", margin + (sigW + colGap) * 2, sigYLine + 22);
  doc.setTextColor(20);

  y += 80;
  if (signature) {
    doc.setDrawColor(16, 185, 129);
    doc.setFillColor(236, 253, 245);
    doc.roundedRect(margin, y - 8, pageW - margin * 2, 36, 4, 4, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(6, 95, 70);
    doc.text("FIRMATO DIGITALMENTE VIA OTP EMAIL", margin + 10, y + 4);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(
      `Firmatario: ${signature.signer_name || signature.signer_email} · ${signature.signer_email}`,
      margin + 10, y + 16,
    );
    doc.text(
      `Firmato il ${fmtDate(signature.signed_at)} ${new Date(signature.signed_at).toLocaleTimeString("it-IT")} · ID firma: ${signature.id}`,
      margin + 10, y + 26,
    );
    y += 44;
  }
  doc.setFontSize(7);
  doc.setTextColor(120);
  doc.text(
    `Generato il ${new Date().toLocaleString("it-IT")} · ${team?.name || ""} · Cartellino conforme alle norme F.I.G.C. - S.G.S.`,
    pageW / 2, y, { align: "center" }
  );

  const safe = `cartellino_${(player?.last_name || "x")}_${(player?.first_name || "y")}`.toLowerCase().replace(/[^a-z0-9_]/g, "_");
  doc.save(`${safe}.pdf`);
}
